import { z } from "zod";

const VAT_FORMAT_MESSAGE =
  "Le numéro de TVA doit être de la forme FR00123456789";

export const sirenSchema = z
  .string()
  .refine((value) => /^(\d{9}|\d{3} \d{3} \d{3})$/.test(value), {
    message: "Un numéro de SIREN contient 9 chiffres",
  });

export const siretSchema = z
  .string()
  .refine((value) => /^(\d{14}|\d{3} \d{3} \d{3} \d{5})$/.test(value), {
    message: "Un numéro de SIRET contient 14 chiffres",
  });

export const sirenOrSiretSchema = z
  .string()
  .refine((value) => value.replace(/\s/g, "").length <= 14, {
    message: "Le numéro ne peut pas dépasser 14 caractères",
  })
  .refine(
    (value) =>
      /^(\d{9}|\d{3} \d{3} \d{3})$/.test(value) ||
      /^(\d{14}|\d{3} \d{3} \d{3} \d{5})$/.test(value),
    {
      message:
        "Veuillez saisir un numéro de SIREN (9 chiffres) ou SIRET (14 chiffres)",
    }
  );

export const vatSchema = z
  .string()
  .min(8, {
    message: VAT_FORMAT_MESSAGE,
  })
  .refine((value) => /^[0-9A-Za-z]+$/.test(value), {
    message: VAT_FORMAT_MESSAGE,
  });

const extractSirenFromSiret = (siret: string): string => {
  const cleaned = siret.replace(/\s/g, "");
  return cleaned.length === 14 ? cleaned.substring(0, 9) : cleaned;
};

export const createVatSchemaWithSiren = (
  ctx: z.RefinementCtx,
  siretValue?: string,
  path = "vat"
) => {
  return z
    .string()
    .min(8, {
      message: VAT_FORMAT_MESSAGE,
    })
    .refine((value) => /^[0-9A-Za-z]+$/.test(value), {
      message: VAT_FORMAT_MESSAGE,
    })
    .superRefine((value) => {
      if (!siretValue) return true;

      const frenchVatPattern = /^FR(\d{2})(\d{9})$/i;
      const match = value.match(frenchVatPattern);

      if (!match) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Le numéro de TVA est invalide",
          path: [path],
        });

        return false;
      }

      const vatSiren = match[2];
      const sirenFromSiret = extractSirenFromSiret(siretValue);

      if (vatSiren !== sirenFromSiret) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Le numéro de TVA doit contenir le SIREN de votre entreprise",
          path: [path],
        });

        return false;
      }

      return true;
    });
};

export const validateVatWithSiren = (
  ctx: z.RefinementCtx,
  vat: string | null | undefined,
  siret: string | null | undefined,
  path = "vat"
) => {
  if (vat && siret) {
    createVatSchemaWithSiren(ctx, siret, path).safeParse(vat);
  }
};
